/**
 * Learn to Land This Job - AI Coach functionality
 * @route 'jobBoard.postings.recommendations'
 */

import {t} from '../../../localization/translate.js';

/** Minimum delay between steps (ms) */
const MIN_STEP_DELAY_MS = 800;

/** Maximum delay between steps (ms) */
const MAX_STEP_DELAY_MS = 2000;

/** State to track current recommendations URL for retry */
let currentRecommendationsUrl = '';
let currentMemberName = '';
let currentJobTitle = '';

/** Promise that resolves when animation completes */
let animationCompletePromise = Promise.resolve();

/**
 * Get the loading steps with personalized content
 * @param {string} memberName
 * @param {string} jobTitle
 * @returns {Array<string>}
 */
function getLoadingSteps(memberName, jobTitle) {
    return [
        t("Loading and analyzing :name's profile...", {name: memberName}),
        t('Analyzing job requirements: :jobTitle...', {jobTitle}),
        t('Comparing them against industry standards...'),
        t("Determining :name's skill gaps...", {name: memberName}),
        t('Mapping out suitable IxDF Courses...'),
        t('Picking relevant Master Classes...'),
        t('Compiling recommendations for :name to land this job...', {name: memberName}),
    ];
}

/**
 * Get a random delay between min and max
 * @returns {number}
 */
function getRandomDelay() {
    return Math.floor(Math.random() * (MAX_STEP_DELAY_MS - MIN_STEP_DELAY_MS + 1)) + MIN_STEP_DELAY_MS;
}

/**
 * Handle the Activate AI Coach button click
 * @param {Event} event
 */
function handleActivateClick(event) {
    const button = /** @type {HTMLButtonElement|null} */ (event.target.closest('.js-activate-ai-coach'));
    if (!button) {
        return;
    }

    // Check if this is a guest redirect
    const guestRedirect = button.dataset.guestRedirect;
    if (guestRedirect) {
        window.location.href = guestRedirect;
        return;
    }

    // Member flow - get recommendations
    const recommendationsUrl = button.dataset.recommendationsUrl || currentRecommendationsUrl;
    const memberName = button.dataset.memberName || currentMemberName;
    const jobTitle = button.dataset.jobTitle || currentJobTitle;

    if (!recommendationsUrl) {
        console.warn('Missing recommendations URL');
        return;
    }

    // Store for retry
    currentRecommendationsUrl = recommendationsUrl;
    currentMemberName = memberName;
    currentJobTitle = jobTitle;

    // Disable button to prevent double clicks
    button.disabled = true;

    // Show loading state and start animation
    showLoadingState(memberName, jobTitle);

    // Fetch recommendations
    fetchRecommendations(recommendationsUrl);
}

/**
 * Show the loading state with animated steps
 * @param {string} memberName
 * @param {string} jobTitle
 */
function showLoadingState(memberName, jobTitle) {
    const container = document.getElementById('js-ai-coach-container');
    const template = /** @type {HTMLTemplateElement|null} */ (document.getElementById('js-ai-coach-loading-template'));

    if (!container || !template) {
        console.warn('Missing container or loading template');
        return;
    }

    // Clone template and insert
    const content = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
    container.replaceChildren(content);

    // Start step animation and store the promise
    const steps = getLoadingSteps(memberName, jobTitle);
    animationCompletePromise = animateSteps(steps);
}

/**
 * Animate the steps appearing one by one, replacing previous text
 * @param {Array<string>} steps
 * @returns {Promise<void>} Resolves when all steps have been shown
 */
async function animateSteps(steps) {
    const textElement = document.getElementById('js-ai-coach-step-text');
    const progressBar = document.getElementById('js-ai-coach-container')?.querySelector('.js-achievementProgressBar');

    if (!textElement) {
        return;
    }

    // Sequential awaits are intentional for step-by-step animation
    /* eslint-disable no-await-in-loop */
    for (let i = 0; i < steps.length; i++) {
        // Fade out current text (skip on first iteration)
        if (i > 0) {
            textElement.classList.remove('opacity-100');
            textElement.classList.add('opacity-0');
            await delay(150);
        }

        // Update text
        textElement.textContent = steps[i];

        // Fade in new text
        textElement.classList.remove('opacity-0');
        textElement.classList.add('opacity-100');

        // Update progress bar (cap at 90% until complete)
        const progress = Math.min(((i + 1) / steps.length) * 100, 90);
        if (progressBar instanceof HTMLElement) {
            progressBar.style.width = `${progress}%`;
            progressBar.setAttribute('aria-valuenow', String(Math.round(progress)));
        }

        // Wait random delay before next step
        await delay(getRandomDelay());
    }
    /* eslint-enable no-await-in-loop */
}

/**
 * Fetch recommendations from the server
 * @param {string} url
 */
async function fetchRecommendations(url) {
    try {
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Accept': 'text/html',
                'X-Requested-With': 'XMLHttpRequest',
            },
            credentials: 'same-origin',
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const html = await response.text();

        // Wait for animation to complete all steps
        await animationCompletePromise;

        // Complete progress bar
        const progressBar = document.getElementById('js-ai-coach-container')?.querySelector('.js-achievementProgressBar');
        if (progressBar instanceof HTMLElement) {
            progressBar.style.width = '100%';
            progressBar.setAttribute('aria-valuenow', '100');
        }

        // Short delay to show 100% before replacing
        await delay(300);

        // Replace entire section with results
        const section = document.getElementById('js-learn-to-land-section');
        if (section) {
            // Get parent to find the new content after outerHTML replacement
            const parent = section.parentElement;
            section.outerHTML = html;

            // Notify lazy loader about new content so images load
            // and initialize cardset sliders for the new content
            if (parent) {
                parent.dispatchEvent(new CustomEvent('lazy:initContent', {bubbles: true}));
                parent.dispatchEvent(new CustomEvent('cardset:init', {bubbles: true}));
            }
        }

    } catch (error) {
        console.warn('Failed to fetch AI recommendations:', error);
        showErrorState();
    }
}

/**
 * Show the error state with retry button
 */
function showErrorState() {
    const container = document.getElementById('js-ai-coach-container');
    const template = /** @type {HTMLTemplateElement|null} */ (document.getElementById('js-ai-coach-error-template'));

    if (!container || !template) {
        return;
    }

    const content = /** @type {DocumentFragment} */ (template.content.cloneNode(true));

    // Set data attributes on retry button
    const retryButton = content.querySelector('.js-activate-ai-coach');
    if (retryButton instanceof HTMLElement) {
        retryButton.dataset.recommendationsUrl = currentRecommendationsUrl;
        retryButton.dataset.memberName = currentMemberName;
        retryButton.dataset.jobTitle = currentJobTitle;
    }

    container.replaceChildren(content);
}

/**
 * Utility function for delays
 * @param {number} ms
 * @returns {Promise<void>}
 */
function delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Initialize the AI Coach functionality
 */
function init() {
    // Use event delegation for dynamically created buttons
    document.addEventListener('click', (event) => {
        const target = /** @type {HTMLElement} */ (event.target);
        if (target.closest('.js-activate-ai-coach')) {
            handleActivateClick(event);
        }
    });
}

// Initialize when DOM is ready
document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', init)
    : init();